import _ from "lodash"

import { Box, Button, Card, CardContent, CardHeader, Container, Divider, Grid, LinearProgress, Paper, Table, Typography } from "@mui/material"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined"
import { useState, useEffect } from "react"
import { useGetIdentity } from "react-admin"
import * as React from "react"
import { dataProvider } from "../../App"
import { parse, unparse } from "papaparse"
import { DriverProfileTable } from "./standalone-driver-profile-table"
import { getAuthorType } from "./helpers/helpers"

const ROW_LIMIT = 1000

const ColumnHeaders = {
  DriverId: "driver_id",
  IsAllowedDeliverAlcohol: "is_driver_allowed_deliver_alcohol",
  IsAllowedDeliverOtherRestrictedProducts: "is_driver_allowed_deliver_other_restricted_products",
  Reason: "reason",
  ValidationError: "validation_error",
}

// Extract a list of driver ids
const getDriverIds = (csvData: any[]) => {
  return _.reduce(
    csvData,
    (driverIdsList, row) => {
      const driverId = _.get(row, ColumnHeaders.DriverId)
      if (driverId) {
        driverIdsList.push(driverId)
      }
      return driverIdsList
    },
    [] as any
  )
}

const FileValidationSucceededText: React.FC<any> = (props: any) => {
  const isVisible = props.isVisible
  return isVisible ? (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <CheckCircleOutlineIcon />
      <Typography align="center">File is valid</Typography>
    </Box>
  ) : null
}

const FileValidationFailedText: React.FC<any> = (props: any) => {
  const isVisible = props.isVisible
  return isVisible ? (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <CancelOutlinedIcon />
      <Typography align="center">File is invalid</Typography>
    </Box>
  ) : null
}

export interface ProcessingFileProps {
  updateState: any[]
  uploadedData: any[]
  hasSomeProcessedUpdates: boolean
  isFinished: boolean
  updateResultsUrl: string
}

// Component that renders processing results
const ProcessingFile: React.FC<ProcessingFileProps> = (props: ProcessingFileProps) => {
  const updateState = props.updateState
  const uploadedData = props.uploadedData
  const hasSomeProcessedUpdates = props.hasSomeProcessedUpdates
  const isFinished = props.isFinished
  const updateResultsUrl = props.updateResultsUrl
  const numRowsFailed = _.filter(updateState, { success: false }).length
  const numRowsSucceeded = _.filter(updateState, { success: true }).length

  return (
    <Box>
      {hasSomeProcessedUpdates && <LinearProgress variant="determinate" value={(updateState.length / uploadedData.length) * 100} />}
      {hasSomeProcessedUpdates && <Typography align="center">{`Processed: ${updateState.length} rows / ${uploadedData.length} total`}</Typography>}
      {hasSomeProcessedUpdates && <Typography align="center">{`Succeeded: ${numRowsSucceeded} rows / ${uploadedData.length} total`}</Typography>}
      {hasSomeProcessedUpdates && <Typography align="center">{`Failed: ${numRowsFailed} rows / ${uploadedData.length} total`}</Typography>}
      <br />
      {isFinished && numRowsFailed > 0 && <Typography align="center">{numRowsFailed} rows have failed to update, download the file to see which rows have failed.</Typography>}
      {isFinished && (numRowsSucceeded === uploadedData.length || numRowsFailed == 0) && <Typography align="center">All rows have succeeded.</Typography>}
      {!_.isEmpty(updateResultsUrl) && <a href={updateResultsUrl}>Download file</a>}
    </Box>
  )
}

// Component that renders table of updated driver profiles values
const ResultsTable: React.FC<any> = (props: any) => {
  const updateState = props.updateState
  const updatedDriverProfiles = _.reduce(
    updateState,
    (aggregation, row) => {
      if (row.updated_profile) {
        aggregation.push(row.updated_profile)
      }
      return aggregation
    },
    [] as any
  ) as any
  return <DriverProfileTable data={updatedDriverProfiles}></DriverProfileTable>
}

export const BulkEditForm: React.FC = (props: any) => {
  const [uploadedFile, setUploadedFile] = useState()
  const [updateState, setUpdateState] = useState([] as any[])
  const [csvValidatedParsedData, setCsvValidatedParsedData] = useState([])
  const [generalErrorState, setGeneralErrorState] = useState({})
  const [hasValidationErrors, setHasValidationErrors] = useState(false)
  const [validationErrorUrl, setValidationErrorUrl] = useState("")
  const [updateResultsUrl, setUpdateResultsUrl] = useState("")
  const { identity } = useGetIdentity()
  const isCompliance = identity?.driverRoles.includes('compliance')

  const onParseComplete = (results: any, file: File) => {
    const cleanedValidatedData = cleanAndValidateParsedCsvData(results) as any
    setCsvValidatedParsedData(cleanedValidatedData)
  }

  const onParseError = (error: any, file: File) => {
    setGeneralErrorState({
      message: "Something went wrong with parsing the CSV, double check the values in the file before retrying.",
      error: error,
    })
  }

  // parsed data may contain extraneous empty columns/rows, clean up data and validate for required values
  const cleanAndValidateParsedCsvData = (results: any) => {
    if (_.isEmpty(results.data)) return []
    const cleanedValidateRows = _.map(results.data, (row, index: number) => {
      const hasCorrectHeaders =
        row.hasOwnProperty(ColumnHeaders.DriverId) &&
        row.hasOwnProperty(ColumnHeaders.IsAllowedDeliverAlcohol) &&
        row.hasOwnProperty(ColumnHeaders.IsAllowedDeliverOtherRestrictedProducts) &&
        row.hasOwnProperty(ColumnHeaders.Reason)
      const validatedData = {
        [ColumnHeaders.DriverId]: _.toString(row[ColumnHeaders.DriverId]),
        [ColumnHeaders.IsAllowedDeliverAlcohol]: row[ColumnHeaders.IsAllowedDeliverAlcohol],
        [ColumnHeaders.IsAllowedDeliverOtherRestrictedProducts]: row[ColumnHeaders.IsAllowedDeliverOtherRestrictedProducts],
        [ColumnHeaders.Reason]: row[ColumnHeaders.Reason],
        [ColumnHeaders.ValidationError]: "",
      }
      // set validation error messages so user can easily correct it
      if (!hasCorrectHeaders) {
        setHasValidationErrors(true)
        validatedData[ColumnHeaders.ValidationError] = `Missing values in this row. `
      }
      if (!_.isNumber(row[ColumnHeaders.DriverId])) {
        setHasValidationErrors(true)
        validatedData[ColumnHeaders.ValidationError] += `${ColumnHeaders.DriverId} should be a number. `
      }
      if (!_.isBoolean(row[ColumnHeaders.IsAllowedDeliverAlcohol] || !_.isBoolean(row[ColumnHeaders.IsAllowedDeliverOtherRestrictedProducts]))) {
        setHasValidationErrors(true)
        validatedData[
          ColumnHeaders.ValidationError
        ] += `${ColumnHeaders.IsAllowedDeliverAlcohol} or ${ColumnHeaders.IsAllowedDeliverOtherRestrictedProducts} should be either true or false. `
      }
      return validatedData
    })
    if (cleanedValidateRows.length > ROW_LIMIT) {
      setGeneralErrorState({
        message: `CSV Upload is limited to processing ${ROW_LIMIT} rows at a time currently, please separate the file according to the row limit.`,
      })
    }
    return cleanedValidateRows
  }

  // when file is uploaded, parse and set results in state in onParseComplete
  useEffect(() => {
    if (!uploadedFile) return
    parse(uploadedFile, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: onParseComplete,
      error: onParseError,
    })
  }, [uploadedFile])

  // create downloadable csv file of validation errors
  useEffect(() => {
    if (hasValidationErrors && !_.isEmpty(csvValidatedParsedData)) {
      const csv = unparse(csvValidatedParsedData)
      const csvData = new Blob([csv], { type: "text/csv;charset=utf-8;" })
      const csvURL = window.URL.createObjectURL(csvData) as string
      setValidationErrorUrl(csvURL)
    }
  }, [hasValidationErrors, csvValidatedParsedData])

  const resetAllStates = () => {
    // reset state upon new file upload
    setUpdateState([])
    setCsvValidatedParsedData([])
    setGeneralErrorState({})
    setHasValidationErrors(false)
    setValidationErrorUrl("")
    setUpdateResultsUrl("")
  }

  const authorType = getAuthorType(identity)
  const uploadOnChange = (event: any) => {
    resetAllStates()
    setUploadedFile(event.target.files[0])
  }
  const uploadedFilename = uploadedFile ? uploadedFile?.["name"] : ""
  const handleSubmitClick = async () => {
    const driverIds = getDriverIds(csvValidatedParsedData)
    const searchRequest = {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "id", order: "ASC" },
      filter: { rails_ids: driverIds },
    }
    let driverProfilesArray = [] as any
    // update requires a mostly full driver profile in order to call update API
    await dataProvider
      .getList(`drivers`, searchRequest)
      .then((response: any) => {
        driverProfilesArray = response.data
      })
      .catch((e) => {
        setGeneralErrorState({
          message: `Having trouble querying driver profiles. Try again later.`,
          error: e,
        })
        console.error("getList drivers error", e, e.message)
      })
    if (_.isEmpty(driverProfilesArray)) {
      setGeneralErrorState({
        message: `Having trouble querying driver profiles. Try again later.`,
      })
      return
    }
    const driverProfilesByRailsId = _.keyBy(driverProfilesArray, "rails_id")

    _.each(csvValidatedParsedData, async (row) => {
      const driverId = _.get(row, ColumnHeaders.DriverId)
      const updatedIsDriverAllowedDeliverAlcohol = _.get(row, ColumnHeaders.IsAllowedDeliverAlcohol)
      const updatedIsDriverAllowedDeliverOtherRestrictedProducts = _.get(row, ColumnHeaders.IsAllowedDeliverOtherRestrictedProducts)
      const reason = _.get(row, ColumnHeaders.Reason)
      const gimProfile = _.get(driverProfilesByRailsId, driverId, {})
      const gimId = _.get(gimProfile, "id")

      if (_.isEmpty(gimProfile)) {
        setUpdateState((prevState) => [
          ...prevState,
          {
            driver_id: driverId,
            success: false,
            message: `Missing GIM profile for railsId ${driverId}`,
            updated_profile: null,
            error: null,
          },
        ])
        return
      }
      if (!_.startsWith(gimId, "gim")) {
        setUpdateState((prevState) => [
          ...prevState,
          {
            driver_id: driverId,
            success: false,
            message: "Needs to migrate account to GIM id",
            updated_profile: null,
            error: null,
          },
        ])
        return
      }
      const audit_logs = {
        reference_id: gimId,
        type: "driver",
        author_type: authorType,
        author: identity?.email,
        text: reason,
      }
      const onlyUpdateFields = []
      const updateRequest = { ...gimProfile, audit_logs: audit_logs, should_update_delivery_zone_ids: false } as any
      if (updatedIsDriverAllowedDeliverAlcohol !== undefined) {
        updateRequest.is_driver_allowed_deliver_alcohol = updatedIsDriverAllowedDeliverAlcohol
        onlyUpdateFields.push("is_driver_allowed_deliver_alcohol")
      }
      if (updatedIsDriverAllowedDeliverOtherRestrictedProducts !== undefined) {
        updateRequest.is_driver_allowed_deliver_other_restricted_products = updatedIsDriverAllowedDeliverOtherRestrictedProducts
        onlyUpdateFields.push("is_driver_allowed_deliver_other_restricted_products")
      }
      updateRequest.only_update_fields = onlyUpdateFields
      await dataProvider
        .update("drivers", { id: gimId, data: updateRequest })
        .then((response: any) => {
          setUpdateState((prevState) => [...prevState, { driver_id: driverId, success: true, message: "", updated_profile: response.data, error: null }])
        })
        .catch((e) => {
          console.error("update/driver error message", e, e.message)
          setUpdateState((prevState) => [...prevState, { driver_id: driverId, success: false, message: "Something went wrong", updated_profile: null, error: e }])
        })
    })
  }

  // when bulk update is finished, upload success results into a downloadable CSV
  useEffect(() => {
    if (!_.isEmpty(updateState) && updateState.length === csvValidatedParsedData.length) {
      const cleanUpdateState = _.map(updateState, (row) => {
        return {
          driver_id: row.driver_id,
          success: row.success,
          messsage: row.message,
        }
      })
      const csv = unparse(cleanUpdateState)
      const csvData = new Blob([csv], { type: "text/csv;charset=utf-8;" })
      const csvURL = window.URL.createObjectURL(csvData) as string
      setUpdateResultsUrl(csvURL)
    }
  }, [updateState])

  const failedValidation = !_.isEmpty(csvValidatedParsedData) && (hasValidationErrors || !_.isEmpty(generalErrorState))
  const succeededValidation = !_.isEmpty(csvValidatedParsedData) && !hasValidationErrors && _.isEmpty(generalErrorState)
  const hasSomeProcessedUpdates = !_.isEmpty(updateState) && updateState.length <= csvValidatedParsedData.length
  const hasFinishedProcessingUpdate = !_.isEmpty(updateState) && updateState.length === csvValidatedParsedData.length
  return isCompliance ? (
    <Container component="main" maxWidth="md" sx={{ mb: 4 }}>
      <Grid container spacing={1} direction="column" alignItems="flex-start">
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 } }}>
            <CardHeader title="Bulk Edit" subheader="Driver Alcohol and Restricted Product Delivery" />
            <CardContent
              children={
                <React.Fragment>
                  <Typography component="p" align="left">
                    {`Upload a CSV of drivers that you would like to bulk toggle their ability to deliver alcohol and other restricted products.
          CSV is limited to ${ROW_LIMIT} rows at a time. The file will be validated before submitting to check if required values are present for all rows. Please fix validation errors before submitting.`}
                  </Typography>
                  <br />
                  <Typography component="p" align="left">
                    {`The CSV must contain a header row matching: ${ColumnHeaders.DriverId},${ColumnHeaders.IsAllowedDeliverAlcohol},${ColumnHeaders.IsAllowedDeliverOtherRestrictedProducts},${ColumnHeaders.Reason}`}
                  </Typography>
                  <br />
                  <Divider />
                  <br />
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button variant="outlined" component="label">
                      Choose CSV File
                      <input type="file" accept=".csv" onChange={uploadOnChange} id="raised-button-file" hidden />
                    </Button>
                  </Box>
                  <Typography align="center">{uploadedFilename}</Typography>
                  <FileValidationFailedText isVisible={failedValidation} />
                  <FileValidationSucceededText isVisible={succeededValidation} />
                  {!_.isEmpty(validationErrorUrl) && (
                    <Typography align="center">
                      <a href={validationErrorUrl}>Download file to see validation errors</a>
                    </Typography>
                  )}
                  <br />
                  {!_.isEmpty(generalErrorState) && <Typography align="center">{_.get(generalErrorState, "message")}</Typography>}
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button variant="contained" disabled={!succeededValidation} onClick={handleSubmitClick}>
                      Submit
                    </Button>
                  </Box>
                </React.Fragment>
              }
            />
          </Card>
        </Grid>
        {(hasSomeProcessedUpdates || hasFinishedProcessingUpdate) && (
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: { xs: 2, md: 3 } }}>
              <CardHeader title="Results" />
              <CardContent
                children={
                  <ProcessingFile
                    updateState={updateState}
                    uploadedData={csvValidatedParsedData}
                    hasSomeProcessedUpdates={hasSomeProcessedUpdates}
                    isFinished={hasFinishedProcessingUpdate}
                    updateResultsUrl={updateResultsUrl}
                  />
                }
              />
            </Card>
          </Grid>
        )}
        <br />
        <br />
        {(hasSomeProcessedUpdates || hasFinishedProcessingUpdate) && (
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: { xs: 2, md: 3 } }}>
              <CardHeader title="Updated Profile Values" />
              <CardContent children={<ResultsTable {...props} updateState={updateState} />} />
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  ) : (
    <Container component="main" maxWidth="md" sx={{ mb: 4 }}>
      <Grid container spacing={1} direction="column" alignItems="flex-start">
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 } }}>
            <CardHeader title="Bulk Edit" subheader="Driver Alcohol and Restricted Product Delivery" />
            <CardContent
              children={
                <Typography component="p" align="center">
                  You don't have permission to view this page.
                </Typography>
              }
            />
          </Card>
        </Grid>
      </Grid>
    </Container>
  )
}
